import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-verification-state-card.css?inline';
import '../pk-card/pk-card.js';

type VerificationState = 'sent' | 'confirmed' | 'expired' | 'resend-prompt';

/**
 * `<pk-verification-state-card>` — email-verification state machine.
 *
 * Renders one of four states tied to an email verification flow:
 *   - `sent`           — "We've emailed dan@fyin.com a verification link."
 *   - `confirmed`      — "Email confirmed. You're all set."
 *   - `expired`        — "That verification link has expired."
 *   - `resend-prompt`  — "Didn't get the email? Resend the verification link."
 *
 * Wraps `<pk-card>` and exposes a `cta` slot for the state-appropriate
 * primary action (typically a `<pk-button>` or `<form>`).
 *
 * Attributes:
 *   - `state` — current verification state (default `sent`).
 *   - `email` — the address being verified; shown inside the body copy.
 *
 * Slots:
 *   - `cta` — state-appropriate primary action button / form.
 */
export class PkVerificationStateCard extends HTMLElement {
    static readonly tagName = 'pk-verification-state-card';

    static get observedAttributes(): string[] {
        return ['state', 'email'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private _state(): VerificationState {
        const raw = this.getAttribute('state') ?? 'sent';
        switch (raw) {
            case 'confirmed':
            case 'expired':
            case 'resend-prompt':
                return raw;
            default:
                return 'sent';
        }
    }

    private _copyFor(state: VerificationState, email: string): { heading: string; lede: string } {
        const target = email || 'your email address';
        switch (state) {
            case 'sent':
                return {
                    heading: 'Verify your email',
                    lede:    `We've emailed ${target} a verification link. Open it from the same device to finish signing in.`,
                };
            case 'confirmed':
                return {
                    heading: 'Email confirmed',
                    lede:    `${target} is verified. You can close this tab or continue to the portal.`,
                };
            case 'expired':
                return {
                    heading: 'That link has expired',
                    lede:    `The verification link for ${target} is no longer valid. Request a new one to continue.`,
                };
            case 'resend-prompt':
                return {
                    heading: 'Didn’t get the email?',
                    lede:    `We can send another verification link to ${target}. Check your spam folder before requesting a resend.`,
                };
        }
    }

    private template(): TemplateResult {
        const state = this._state();
        const email = this.getAttribute('email') ?? '';
        const { heading, lede } = this._copyFor(state, email);
        return html`
            <style>${styles}</style>
            <pk-card>
                <div class="state state-${state}">
                    <span class="state-glyph state-glyph-${state}" aria-hidden="true"></span>
                    <h2 class="heading">${heading}</h2>
                    <p class="lede">${lede}</p>
                    <div class="cta-wrap">
                        <slot name="cta"></slot>
                    </div>
                </div>
            </pk-card>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkVerificationStateCard.tagName)) {
    customElements.define(PkVerificationStateCard.tagName, PkVerificationStateCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-verification-state-card': PkVerificationStateCard;
    }
}
